import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import {
  Crown,
  DollarSign,
  Users,
  Globe,
  Trophy,
  TrendingUp,
  Radio,
  Plane,
  GraduationCap,
  Handshake,
  Landmark,
  ArrowRight,
  Loader2,
  CheckCircle2,
  Mail,
} from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LEAGUES = [
  {
    key: "AAMBL",
    name: "AAMBL",
    full: "Afro Am Men's Basketball League",
    tagline: "Elite talent · global scouting · community impact",
    accent: "text-amber-400",
    accentBorder: "border-amber-400",
    accentBg: "bg-amber-400",
  },
  {
    key: "WAABL",
    name: "WAABL",
    full: "Women's Afro Am Basketball League",
    tagline: "Empowering women · elevating the game",
    accent: "text-purple-400",
    accentBorder: "border-purple-400",
    accentBg: "bg-purple-400",
  },
  {
    key: "BOTH",
    name: "Both Leagues",
    full: "AAMBL + WAABL",
    tagline: "Explore ownership across both leagues",
    accent: "text-white",
    accentBorder: "border-white",
    accentBg: "bg-white",
  },
];

const BENEFITS = [
  { icon: DollarSign, title: "Financial Opportunity", body: "Multiple revenue streams from ticket sales, sponsorships, merchandise, media rights, and more." },
  { icon: Users, title: "Community Impact", body: "Give back, inspire youth, and strengthen communities through basketball and outreach programs." },
  { icon: Globe, title: "Global Impact", body: "Gain visibility across Africa, the U.S., and international markets through media and league partnerships." },
  { icon: Trophy, title: "Player Development", body: "Access top talent through scouting, training, and international player development." },
  { icon: TrendingUp, title: "Brand & Business Growth", body: "Align your brand with a powerful platform that drives recognition, loyalty, and new business opportunities." },
  { icon: Radio, title: "Media & Broadcast Exposure", body: "Appear on live streams, broadcasts, social media, and highlight platforms nationwide." },
  { icon: Plane, title: "Travel & Cultural Exchange", body: "Engage in international tours, exhibitions, and cultural exchange experiences." },
  { icon: GraduationCap, title: "Youth & Academy Pipeline", body: "Build your legacy by developing the next generation of athletes through camps and academies." },
  { icon: Handshake, title: "Networking & Partnerships", body: "Connect with business leaders, investors, community leaders, and global partners." },
  { icon: Landmark, title: "Legacy Building", body: "Be part of history by building a franchise that lasts for generations." },
];

const INVESTMENT_RANGES = [
  "Under $100k",
  "$100k – $250k",
  "$250k – $500k",
  "$500k – $1M",
  "$1M – $5M",
  "$5M+",
  "Prefer to discuss",
];

const OWNERSHIP_TYPES = [
  "Sole Owner",
  "Lead Owner (Group)",
  "Ownership Group Member",
  "Investor (Non-operating)",
  "Corporate / Institutional",
  "Not sure yet",
];

const TeamOwnerRegisterPage = () => {
  const [form, setForm] = useState({
    league_preference: "AAMBL",
    market_preference: "",
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    company: "",
    title: "",
    city_state: "",
    investment_range: "",
    ownership_type: "",
    business_background: "",
    sports_background: "",
    why_afroam: "",
    linkedin: "",
    website: "",
    agree_to_terms: false,
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    for (const k of ["first_name", "last_name", "email", "phone"]) {
      if (!String(form[k] || "").trim()) {
        toast.error(`Please complete: ${k.replace("_", " ")}.`);
        return;
      }
    }
    if (!form.agree_to_terms) {
      toast.error("Please acknowledge the certification.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/team-owners`, form);
      toast.success("Inquiry received. We'll be in touch shortly.");
      setSubmitted(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Submission failed.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-neutral-800 focus:border-amber-400 text-white py-3 outline-none placeholder:text-neutral-700 [color-scheme:dark]";
  const labelCls = "text-[10px] uppercase tracking-[0.3em] text-neutral-400";

  return (
    <div className="App bg-black text-white min-h-screen">
      <Navbar />

      {/* Hero */}
      <section className="pt-32 md:pt-40 pb-16 border-b border-neutral-900 bg-gradient-to-b from-[#0a0a0a] to-black">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.45em] text-amber-400 mb-6">
            <span className="w-8 h-px bg-amber-400" />
            <Crown size={14} />
            <span>Team Owners · Recruitment</span>
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            data-testid="owners-title"
            className="font-display uppercase text-5xl md:text-7xl lg:text-8xl leading-[0.88] text-white"
          >
            Own the game.
            <br />
            <span className="text-amber-400">Build a legacy.</span>
          </motion.h1>
          <p className="text-neutral-300 max-w-2xl mt-6 font-light leading-relaxed">
            AAMBL &amp; WAABL — two leagues, one mission, global impact. Both
            leagues launch <span className="text-white">Mid-Spring 2027</span>.
            Founding ownership groups are being assembled now.
          </p>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl">
            {LEAGUES.slice(0, 2).map((l) => (
              <div key={l.key} className={`border ${l.accentBorder}/40 bg-[#0a0a0a] p-5`}>
                <div className={`font-display uppercase text-3xl ${l.accent}`}>
                  {l.name}
                </div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-400 mt-1">
                  {l.full}
                </div>
                <div className="text-xs text-neutral-500 mt-3 leading-relaxed">
                  {l.tagline}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 md:py-24 border-b border-neutral-900 bg-[#070707]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="text-[10px] uppercase tracking-[0.4em] text-amber-400 mb-5">
            — Benefits of Owning a Team
          </div>
          <h2 className="font-display uppercase text-4xl md:text-6xl text-white leading-[0.9] max-w-3xl">
            Ten reasons
            <br />
            <span className="text-amber-400">to own in.</span>
          </h2>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-px bg-neutral-900 border border-neutral-900" data-testid="owners-benefits">
            {BENEFITS.map((b, i) => {
              const Icon = b.icon;
              return (
                <motion.div
                  key={b.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: (i % 5) * 0.05, duration: 0.5 }}
                  className="bg-black p-6 hover:bg-[#0a0a0a] transition-colors"
                >
                  <Icon size={18} className="text-amber-400" />
                  <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mt-4">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="font-display uppercase text-lg text-white mt-1 leading-tight">
                    {b.title}
                  </div>
                  <p className="text-xs text-neutral-400 mt-3 leading-relaxed">
                    {b.body}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Form */}
      {submitted ? (
        <section className="py-16 md:py-24 bg-black border-b border-neutral-900">
          <div className="max-w-[900px] mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="relative border border-amber-400/40 bg-[#0a0a0a] p-8 md:p-12"
              data-testid="owners-success"
            >
              <CheckCircle2 size={42} className="text-amber-400 mb-6" />
              <h2 className="font-display uppercase text-4xl md:text-6xl text-white leading-[0.95]">
                Inquiry
                <br />
                <span className="text-amber-400">received.</span>
              </h2>
              <p className="text-neutral-300 mt-6 leading-relaxed">
                Thanks <span className="text-white">{submitted.first_name}</span>{" "}
                — your interest in{" "}
                <span className="text-white">{submitted.league_preference}</span>{" "}
                ownership has been logged. The executive board will reach out at{" "}
                <span className="text-white">{submitted.email}</span> to schedule
                a private conversation.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href="mailto:afroambasketball@gmail.com?subject=AAMBL%20%2F%20WAABL%20Ownership%20Inquiry"
                  className="inline-flex items-center gap-2 bg-amber-400 text-black px-6 py-3.5 text-xs uppercase tracking-[0.28em] font-bold hover:bg-amber-300 transition-colors"
                >
                  <Mail size={14} /> Email Executive Board
                </a>
                <a
                  href="/"
                  className="inline-flex items-center gap-2 border border-white text-white px-6 py-3.5 text-xs uppercase tracking-[0.28em] font-bold hover:bg-white hover:text-black transition-colors"
                >
                  Back Home
                </a>
              </div>
            </motion.div>
          </div>
        </section>
      ) : (
        <section className="py-14 md:py-20 border-b border-neutral-900" id="apply">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
            {/* Left: flyer + league selector */}
            <div className="lg:col-span-5">
              <div className="text-[10px] uppercase tracking-[0.4em] text-neutral-500 mb-4">
                — Step 01 · Choose your league
              </div>

              <div className="space-y-3">
                {LEAGUES.map((l) => {
                  const active = form.league_preference === l.key;
                  return (
                    <button
                      key={l.key}
                      type="button"
                      onClick={() => setField("league_preference", l.key)}
                      data-testid={`league-${l.key.toLowerCase()}`}
                      className={`w-full text-left p-5 border transition-all ${
                        active
                          ? `${l.accentBorder} bg-[#0a0a0a]`
                          : "border-neutral-800 bg-black hover:border-neutral-600"
                      }`}
                    >
                      <div
                        className={`text-[10px] uppercase tracking-[0.35em] mb-2 ${
                          active ? l.accent : "text-neutral-500"
                        }`}
                      >
                        {active ? "Selected" : "Available"}
                      </div>
                      <div className="font-display uppercase text-2xl md:text-3xl text-white">
                        {l.name}
                      </div>
                      <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-400 mt-1">
                        {l.full}
                      </div>
                      <div className="text-xs text-neutral-500 mt-2">
                        {l.tagline}
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="hidden lg:block mt-8">
                <img
                  src="/brand/team-owners-flyer.jpg"
                  alt="AfroAm Team Owners Recruitment"
                  className="w-full h-auto border border-neutral-900"
                  data-testid="owners-flyer"
                />
              </div>
            </div>

            {/* Right: form */}
            <div className="lg:col-span-7">
              <div className="text-[10px] uppercase tracking-[0.4em] text-neutral-500 mb-4">
                — Step 02 · Your details
              </div>
              <form onSubmit={submit} className="space-y-8" data-testid="owners-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className={labelCls}>First Name *</label>
                    <input
                      className={inputCls}
                      value={form.first_name}
                      onChange={(e) => setField("first_name", e.target.value)}
                      placeholder="Alex"
                      data-testid="own-first"
                      required
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Last Name *</label>
                    <input
                      className={inputCls}
                      value={form.last_name}
                      onChange={(e) => setField("last_name", e.target.value)}
                      placeholder="Morgan"
                      data-testid="own-last"
                      required
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Email *</label>
                    <input
                      type="email"
                      className={inputCls}
                      value={form.email}
                      onChange={(e) => setField("email", e.target.value)}
                      placeholder="you@company.com"
                      data-testid="own-email"
                      required
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Phone *</label>
                    <input
                      className={inputCls}
                      value={form.phone}
                      onChange={(e) => setField("phone", e.target.value)}
                      placeholder="(555) 123-4567"
                      data-testid="own-phone"
                      required
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Company / Fund</label>
                    <input
                      className={inputCls}
                      value={form.company}
                      onChange={(e) => setField("company", e.target.value)}
                      placeholder="Morgan Capital Group"
                      data-testid="own-company"
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Title / Role</label>
                    <input
                      className={inputCls}
                      value={form.title}
                      onChange={(e) => setField("title", e.target.value)}
                      placeholder="Managing Partner"
                      data-testid="own-title"
                    />
                  </div>
                  <div>
                    <label className={labelCls}>City / State</label>
                    <input
                      className={inputCls}
                      value={form.city_state}
                      onChange={(e) => setField("city_state", e.target.value)}
                      placeholder="Las Vegas, NV"
                      data-testid="own-city"
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Preferred Team Market</label>
                    <input
                      className={inputCls}
                      value={form.market_preference}
                      onChange={(e) => setField("market_preference", e.target.value)}
                      placeholder="e.g., Houston · Atlanta · Lagos"
                      data-testid="own-market"
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Investment Range</label>
                    <select
                      className={`${inputCls} appearance-none`}
                      value={form.investment_range}
                      onChange={(e) => setField("investment_range", e.target.value)}
                      data-testid="own-investment"
                    >
                      <option value="">Select range</option>
                      {INVESTMENT_RANGES.map((r) => (
                        <option key={r}>{r}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Ownership Type</label>
                    <select
                      className={`${inputCls} appearance-none`}
                      value={form.ownership_type}
                      onChange={(e) => setField("ownership_type", e.target.value)}
                      data-testid="own-type"
                    >
                      <option value="">Select type</option>
                      {OWNERSHIP_TYPES.map((r) => (
                        <option key={r}>{r}</option>
                      ))}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className={labelCls}>Business Background</label>
                    <textarea
                      className={`${inputCls} resize-none`}
                      value={form.business_background}
                      onChange={(e) => setField("business_background", e.target.value)}
                      placeholder="Companies founded/operated, exits, industries..."
                      rows={3}
                      data-testid="own-business"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className={labelCls}>Sports / Team Ownership Experience</label>
                    <textarea
                      className={`${inputCls} resize-none`}
                      value={form.sports_background}
                      onChange={(e) => setField("sports_background", e.target.value)}
                      placeholder="Prior team stakes, coaching, agency, athlete relations..."
                      rows={3}
                      data-testid="own-sports"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className={labelCls}>Why AfroAm?</label>
                    <textarea
                      className={`${inputCls} resize-none`}
                      value={form.why_afroam}
                      onChange={(e) => setField("why_afroam", e.target.value)}
                      placeholder="What draws you to owning an AAMBL / WAABL franchise?"
                      rows={3}
                      data-testid="own-why"
                    />
                  </div>
                  <div>
                    <label className={labelCls}>LinkedIn</label>
                    <input
                      className={inputCls}
                      value={form.linkedin}
                      onChange={(e) => setField("linkedin", e.target.value)}
                      placeholder="https://linkedin.com/in/..."
                      data-testid="own-linkedin"
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Company Website</label>
                    <input
                      className={inputCls}
                      value={form.website}
                      onChange={(e) => setField("website", e.target.value)}
                      placeholder="https://..."
                      data-testid="own-website"
                    />
                  </div>
                </div>

                <label className="flex items-start gap-3 text-xs text-neutral-300 leading-relaxed cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.agree_to_terms}
                    onChange={(e) => setField("agree_to_terms", e.target.checked)}
                    className="mt-0.5 w-4 h-4 accent-amber-400"
                    data-testid="own-agree"
                  />
                  <span>
                    I confirm the information above is accurate and understand
                    this is a preliminary inquiry — not a binding commitment.
                    The AfroAm executive board will contact me for a private
                    discussion.
                  </span>
                </label>

                <div className="pt-4 border-t border-neutral-900 flex flex-wrap items-center gap-4">
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="own-submit"
                    className="inline-flex items-center gap-3 bg-amber-400 text-black px-8 py-4 text-xs uppercase tracking-[0.28em] font-bold hover:bg-amber-300 transition-colors disabled:opacity-60"
                  >
                    {loading ? (
                      <Loader2 className="animate-spin" size={14} />
                    ) : (
                      <ArrowRight size={14} />
                    )}
                    Submit Ownership Inquiry
                  </button>
                  <span className="text-[10px] uppercase tracking-[0.28em] text-neutral-500">
                    Applying for{" "}
                    <span className="text-amber-400">
                      {LEAGUES.find((l) => l.key === form.league_preference)?.name}
                    </span>
                  </span>
                </div>
              </form>
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default TeamOwnerRegisterPage;
